import { defineComponent as _defineComponent } from 'vue'
import { renderSlot as _renderSlot, unref as _unref, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, withCtx as _withCtx, createSlots as _createSlots, createBlock as _createBlock } from "vue"

const _hoisted_1 = ["placeholder", "aria-invalid", "aria-describedby"]

import { computed, nextTick, onMounted, ref, watch } from 'vue';
import FieldContainer from '@src/components/patterns/FieldContainer.vue';
import { useAriaDescribedby } from '@src/components/shared/composables/useAriaDescribedby';

interface Props {
	modelValue: string;
	showError: boolean;
	bankName: string;
	bic: string;
	ariaDescribedby?: string | undefined;
	isMaxWidthField?: boolean;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'IbanField',
  props: {
    modelValue: {},
    showError: { type: Boolean },
    bankName: {},
    bic: {},
    ariaDescribedby: {},
    isMaxWidthField: { type: Boolean }
  },
  emits: [ 'field-changed', 'input', 'blur', 'update:modelValue' ],
  setup(__props: any, { emit: __emit }) {


const props = __props;
const emit = __emit;

const fieldModel = ref<string>( props.modelValue );
const field = ref<HTMLInputElement>( null );
const hasMessage = computed<boolean>( () => props.modelValue !== '' && props.bankName !== '' );
const ariaDescribedby = useAriaDescribedby(
	'iban',
	computed<boolean>( () => false ),
	computed<boolean>( () => props.showError ),
	computed<boolean>( () => hasMessage.value ),
	computed<string | undefined>( () => props.ariaDescribedby )
);

const getDisplayValue = ( newValue: string ) => {
	return newValue.replace( /(.{4})/g, '$& ' ).trim();
};

/**
 * 1. Get the text before the cursor
 * 2. Clear the spaces
 * 3. Add new spaces in the correct positions, but don't trim
 * 4. Return the length
 *
 * @param newValue
 * @param cursorPosition
 */
const getNewCursorPosition = ( newValue: string, cursorPosition: number ): number => {
	return newValue
		.slice( 0, cursorPosition )
		.replace( /\s/g, '' )
		.replace( /(.{4})/g, '$& ' )
		.length;
};

const setFieldValueFromModel = () => {
	field.value.value = getDisplayValue( fieldModel.value );
};

const onInput = async (): Promise<void> => {
	const newValue = field.value.value;
	const cursorPosition = field.value.selectionStart;

	const value = newValue.replace( /\s/g, '' );
	const shouldUpdateFormattedNumber = value.length > fieldModel.value.length;
	fieldModel.value = value;
	await nextTick();

	// Only update the formatted number when the donor is entering text not deleting it
	if ( shouldUpdateFormattedNumber ) {
		setFieldValueFromModel();

		// When we replace the field value the browser jumps the cursor to the end
		// so we reset it after changing the value. This allows the donor to edit
		// their bank number if they spot a mistake.
		const newCursorPosition = getNewCursorPosition( newValue, cursorPosition );
		field.value.setSelectionRange( newCursorPosition, newCursorPosition );
	}

	emit( 'update:modelValue', fieldModel.value );
	emit( 'input' );

	if ( props.showError ) {
		emit( 'field-changed', 'iban' );
	}
};

const onBlur = (): void => {
	setFieldValueFromModel();
	emit( 'field-changed', 'iban' );
	emit( 'blur' );
};

onMounted( () => {
	setFieldValueFromModel();
} );

watch( () => props.modelValue, ( newModelValue: string ) => {
	if ( fieldModel.value !== newModelValue ) {
		fieldModel.value = newModelValue;
		setFieldValueFromModel();
	}
} );


return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(FieldContainer, {
    "input-id": "iban",
    id: "payment-form-iban",
    "show-error": __props.showError,
    "is-max-width-field": __props.isMaxWidthField
  }, _createSlots({
    field: _withCtx(() => [
      _createElementVNode("input", {
        ref_key: "field",
        ref: field,
        name: "iban",
        id: "iban",
        type: "text",
        autocomplete: "on",
        placeholder: _ctx.$t( 'donation_form_payment_bankdata_account_iban_placeholder' ),
        "aria-invalid": __props.showError,
        "aria-describedby": _unref(ariaDescribedby),
        onBlur: onBlur,
        onInput: onInput,
        onPaste: onInput
      }, null, 40, _hoisted_1)
    ]),
    error: _withCtx(() => [
      _createTextVNode(_toDisplayString(_ctx.$t( 'donation_form_payment_iban_error' )), 1)
    ]),
    message: _withCtx(() => [
      (hasMessage.value)
        ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
            _createTextVNode(_toDisplayString(__props.bankName) + " (" + _toDisplayString(__props.bic) + ") ", 1)
          ], 64))
        : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
            _createTextVNode("   ")
          ], 64))
    ]),
    _: 2
  }, [
    (_ctx.$slots.label)
      ? {
          name: "label",
          fn: _withCtx(() => [
            _renderSlot(_ctx.$slots, "label")
          ]),
          key: "0"
        }
      : undefined
  ]), 1032, ["show-error", "is-max-width-field"]))
}
}

})